import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import { api, ApiError } from "../api";
import { useResource } from "../hooks/use-resource";
import type { WechatPaymentOrder, WechatRefund } from "../types";
import {
  ActionButton,
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
} from "./design-system";
import { FilterPopover } from "./filter-popover";

const money = (cents: number) => `¥${(cents / 100).toFixed(2)}`;
const orderStatus = {
  pending: "待支付",
  paid: "已支付",
  failed: "支付失败",
  cancelled: "已取消",
};
const orderStatusOptions = [
  { value: "all", label: "所有订单" },
  ...Object.entries(orderStatus).map(([value, label]) => ({ value, label })),
];
const refundStatus: Record<WechatRefund["status"], string> = {
  pending: "退款待提交",
  processing: "退款中",
  success: "退款成功",
  closed: "退款关闭 · 额度已恢复",
  abnormal: "退款异常 · 请在商户平台处理",
};

export function WechatPaymentOrders({
  onRefunded,
  product = "auto-dorm-check",
}: {
  onRefunded: () => void;
  product?: "auto-dorm-check" | "course-grab";
}) {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState<WechatPaymentOrder | null>(null);
  const selectionKey = JSON.stringify([page, query, status, product]);
  const orders = useResource(
    async () => ({
      selectionKey,
      result: await api.wechatOrders(page, query, status, product),
    }),
    [page, query, status, product],
    15000,
  );
  const result =
    orders.data?.selectionKey === selectionKey ? orders.data.result : null;
  return (
    <section className="rounded-2xl border border-sand-light bg-paper p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-ink">支付订单</h2>
        <ActionButton
          busy={orders.refreshing}
          onClick={() => void orders.refresh()}
        >
          刷新
        </ActionButton>
      </div>
      <form
        className="my-4 flex flex-wrap gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setQuery(draft.trim());
        }}
      >
        <input
          aria-label="搜索支付订单"
          className="min-w-0 flex-1 rounded-xl border border-sand bg-transparent px-3 py-2 text-sm"
          placeholder="姓名、学号或订单号"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        <FilterPopover
          label="订单状态"
          hideLabel
          className="shrink-0"
          triggerClassName="h-full min-w-36 justify-between rounded-xl"
          value={status}
          options={orderStatusOptions}
          onChange={(value) => {
            setPage(1);
            setStatus(value);
          }}
        />
        <ActionButton type="submit">搜索</ActionButton>
      </form>
      {orders.error ? (
        <ErrorState
          error={orders.error}
          onRetry={() => void orders.refresh()}
        />
      ) : null}
      {!result && !orders.error ? <LoadingState rows={3} /> : null}
      {result && !result.items.length ? (
        <EmptyState title="暂无订单" description="可以更换搜索条件" />
      ) : null}
      <div className="divide-y divide-sand-light">
        {(result?.items || []).map((order) => (
          <article
            key={order.id}
            className="flex flex-wrap items-start justify-between gap-4 py-4"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
                <strong>{order.displayName || order.account}</strong>
                <span>{order.planName}</span>
                {order.paymentChannel ? (
                  <span>
                    {order.paymentChannel === "apple_iap"
                      ? "Apple 支付"
                      : "微信支付"}
                  </span>
                ) : null}
                <span className="text-ink-faint">
                  {orderStatus[order.status]}
                </span>
              </div>
              <p className="mt-1 text-xs text-ink-faint">
                {order.studentNumber || "—"} ·{" "}
                {new Date(order.createdAt).toLocaleString()}
              </p>
              <p className="mt-1 break-all font-mono text-xs text-ink-faint">
                {order.outTradeNo}
              </p>
              {order.status === "paid" && !order.deliveryReportedAt ? (
                <p className="mt-1 text-xs text-clay-deep">
                  交付状态待同步
                  {order.lastError ? "，请检查小程序发货管理权限" : ""}
                </p>
              ) : null}
              {order.refunds.map((refund) => (
                <p key={refund.id} className="mt-1 text-xs text-clay-deep">
                  {refundStatus[refund.status]} · {money(refund.amountCents)}
                  {refund.lastError ? " · 需检查商户平台" : ""}
                </p>
              ))}
              {order.paymentChannel === "apple_iap" &&
              order.status === "paid" &&
              !order.refundedCents ? (
                <p className="mt-1 text-xs text-ink-faint">
                  退款由用户在 App Store 申请
                </p>
              ) : null}
            </div>
            <div className="flex flex-none items-center gap-3 whitespace-nowrap">
              <div className="text-right">
                <strong className="text-sm tabular-nums">
                  {money(order.amountCents)}
                </strong>
                {order.refundedCents > 0 ? (
                  <p className="text-xs text-ink-faint">
                    已退 {money(order.refundedCents)}
                  </p>
                ) : null}
              </div>
              {order.status === "paid" &&
              order.paymentChannel !== "apple_iap" &&
              order.credited &&
              !order.refundedCents &&
              !order.refunds.some((refund) => refund.status !== "closed") ? (
                <ActionButton onClick={() => setSelected(order)}>
                  退款
                </ActionButton>
              ) : null}
            </div>
          </article>
        ))}
      </div>
      {result ? (
        <Pagination
          page={page}
          pages={Math.max(1, result.pagination.totalPages)}
          total={result.pagination.total}
          onChange={setPage}
        />
      ) : null}
      {selected ? (
        <RefundDialog
          key={selected.id}
          order={selected}
          product={product}
          onClose={() => setSelected(null)}
          onSaved={() => {
            void orders.refresh();
            onRefunded();
          }}
        />
      ) : null}
    </section>
  );
}

interface SavedRefundRequest {
  key: string;
  reason: string;
  expectedAmountCents: number;
}
function savedRequest(key: string): SavedRefundRequest | null {
  try {
    const value = JSON.parse(
      sessionStorage.getItem(key) || "null",
    ) as SavedRefundRequest | null;
    return value &&
      typeof value.key === "string" &&
      typeof value.reason === "string" &&
      Number.isSafeInteger(value.expectedAmountCents)
      ? value
      : null;
  } catch {
    return null;
  }
}

function RefundDialog({
  order,
  onClose,
  onSaved,
  product,
}: {
  order: WechatPaymentOrder;
  onClose: () => void;
  onSaved: () => void;
  product: "auto-dorm-check" | "course-grab";
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const storageKey =
    product === "course-grab"
      ? `easy-swu:course-grab-refund:v1:${order.id}`
      : `easy-swu:wechat-refund:v1:${order.id}`;
  const [pending, setPending] = useState(() => savedRequest(storageKey));
  const [reason, setReason] = useState(pending?.reason || "");
  const [busy, setBusy] = useState(false);
  const quote = useResource(
    () => api.wechatRefundQuote(order.id, product),
    [order.id, product],
  );
  useEffect(() => {
    const dialog = ref.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  async function submit() {
    if (busy || (!pending && (!quote.data?.refundable || !reason.trim())))
      return;
    const request = pending || {
      key: crypto.randomUUID(),
      reason: reason.trim(),
      expectedAmountCents: quote.data!.amountCents,
    };
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(request));
    } catch {
      toast.error("无法保存退款请求，请重试");
      return;
    }
    setPending(request);
    setBusy(true);
    try {
      const result = await api.refundWechatOrder(
        order.id,
        {
          reason: request.reason,
          expectedAmountCents: request.expectedAmountCents,
        },
        request.key,
        product,
      );
      sessionStorage.removeItem(storageKey);
      toast.success(refundStatus[result.status]);
      onSaved();
      onClose();
    } catch (error) {
      if (
        error instanceof ApiError &&
        [
          "WECHAT_PAY_REFUND_QUOTE_CHANGED",
          "WECHAT_PAY_REFUND_UNAVAILABLE",
          "COURSE_GRAB_REFUND_UNAVAILABLE",
        ].includes(error.code)
      ) {
        sessionStorage.removeItem(storageKey);
        setPending(null);
        void quote.refresh();
      }
      toast.error(
        error instanceof Error ? error.message : "退款结果待确认，请重试",
      );
    } finally {
      setBusy(false);
    }
  }
  const amount = pending?.expectedAmountCents ?? quote.data?.amountCents;
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      className="m-auto max-h-[calc(100vh-24px)] w-[min(480px,calc(100vw-24px))] overflow-auto rounded-2xl border border-sand-light bg-paper p-6 text-ink shadow-2xl backdrop:bg-ink/35"
    >
      <h2 id={titleId} className="text-lg font-semibold">
        退还未使用额度
      </h2>
      <p className="mt-2 text-sm text-ink-soft">
        {order.displayName || order.account} · {order.planName}
      </p>
      {quote.loading && !pending ? <LoadingState rows={2} /> : null}
      {quote.error ? (
        <ErrorState error={quote.error} onRetry={() => void quote.refresh()} />
      ) : null}
      {amount !== undefined ? (
        <p className="my-4 text-2xl font-semibold tabular-nums">
          {money(amount)}
        </p>
      ) : null}
      <p className="mb-4 text-sm text-ink-soft">
        退款将退回原支付账户，并回收对应的未使用额度。
      </p>
      {!pending && quote.data && !quote.data.refundable ? (
        <p className="text-sm text-destructive">
          {quote.data.unavailableReason}
        </p>
      ) : null}
      <label className="block text-sm">
        退款原因
        <textarea
          autoFocus
          maxLength={500}
          className="mt-2 w-full rounded-xl border border-sand bg-transparent p-3"
          value={reason}
          disabled={busy || Boolean(pending)}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
      <div className="mt-5 flex justify-end gap-2">
        <ActionButton disabled={busy} onClick={onClose}>
          返回
        </ActionButton>
        <ActionButton
          tone="danger"
          busy={busy}
          disabled={!pending && (!quote.data?.refundable || !reason.trim())}
          onClick={() => void submit()}
        >
          {pending ? "重试退款" : "确认退款"}
        </ActionButton>
      </div>
    </dialog>
  );
}
